import * as React from 'react';
import { animated, useSpring } from 'react-spring';

import { useDetectPrint } from 'lib/use-detect-print';
import { useRouter } from 'next/router';

import Icon from 'components/icon/icon';
import { Header3, P1 } from 'components/typography';
import FormattedText from 'components/typography/formatted-text';
import { TextProps } from 'components/typography/text';

type Props = {
  title: React.ReactNode | undefined;
  content: string;
  as?: TextProps['as'];
  id?: string;
};

const Accordion: React.FC<Props> = (props) => {
  const router = useRouter();
  const [isContentVisible, setIsContentVisible] = React.useState(false);
  const { rotation } = useSpring({
    rotation: !isContentVisible ? 45 : 0,
    config: { tension: 300, friction: 10 },
  });

  const handleTitleClick = () => {
    setIsContentVisible(!isContentVisible);
  };
  const handleTitleClickWithId = (event: React.MouseEvent) => {
    if (!isContentVisible) {
      setIsContentVisible(event.currentTarget.parentElement?.id === props.id);
    } else {
      setIsContentVisible(false);
    }

    if (event.currentTarget.parentElement?.id === props.id) {
      window.location.hash = `#${props.id}`;
    }
  };
  const isPrinting = useDetectPrint();
  React.useEffect(() => {
    if (global.document.body.classList.contains('is-print-version')) {
      setIsContentVisible(true);
    }
  }, []);
  React.useEffect(() => {
    const getHash = () => window.location.hash.replace('#', '');
    const handleHashComparison = () => setIsContentVisible(props.id === getHash());

    handleHashComparison();

    const handleNextHashChange = () => handleHashComparison();
    // detect window hash changes that next is not aware of,
    // this allows to detect link clicks with fragments,
    // while ignoring back navigations fragment changes
    const handleWindowHashChange = () => handleHashComparison();

    router.events.on('hashChangeStart', handleNextHashChange);
    window.addEventListener('hashchange', handleWindowHashChange);

    return () => {
      router.events.off('hashChangeStart', handleNextHashChange);
      window.removeEventListener('hashchange', handleWindowHashChange);
    };
  }, [router, props.id]);

  return (
    <section className="accorrdion" id={props.id}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- legacy accordion uses a div with onClick; adding keyboard support requires a refactor */}
      <div
        className="accorrdion__header"
        onClick={props.id ? handleTitleClickWithId : handleTitleClick}
      >
        <span className="accorrdion__title">
          <Header3 as="span">{props.title}</Header3>
        </span>
        <span className="accorrdion__close">
          <Header3 as="span">
            <animated.div
              style={{
                transform: rotation.to((degrees) => `rotate(${degrees}deg)`),
              }}
            >
              <Icon name={Icon.Name.Close} size="md" />
            </animated.div>
          </Header3>

          {/* <Icon name={Icon.Name.} size="md" /> */}
        </span>
      </div>

      {isContentVisible || isPrinting ? (
        <div className="accorrdion__content">
          <FormattedText>
            <P1 html={props.content} as="p" />
          </FormattedText>
        </div>
      ) : null}
    </section>
  );
};

export default Accordion;
